import type { ChangeEvent } from "react";
import type { FieldPath, FieldPathValue, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import { TextArea, type TextAreaHeight, type TextAreaProps } from "@/design-system/TextArea";
import { mergeRefs } from "@/helpers/mergeRefs";

import { FormErrorWrapper } from "./FormErrorWrapper";

interface FormTextAreaProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<TextAreaProps, "appearance" | "height" | "invalid" | "startAdornment" | "width"> {
  name: TName;
  rules?: RegisterOptions<TFormValues, TName>;
  onChange?: (value: ChangeEvent<HTMLTextAreaElement>) => void;
  "data-testid"?: string;
  compactHeight?: boolean;
  icon?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "post";
  width?: "full" | "wide";
  maxHeight?: "editor";
  scrollbar?: boolean;
  defaultValue?: FieldPathValue<TFormValues, TName>;
  ref?: React.Ref<HTMLTextAreaElement>;
}
export function FormTextArea<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  "data-testid": dataTestid,
  name,
  rules,
  defaultValue,
  compactHeight,
  icon,
  size = "sm",
  width,
  maxHeight,
  scrollbar,
  readOnly,
  disabled,
  ref,
  ...props
}: FormTextAreaProps<TFormValues, TName>): React.ReactNode {
  const {
    field,
    fieldState: { error },
  } = useController<TFormValues, TName>({
    name,
    rules,
    defaultValue,
  });
  const maxLengthValue = typeof rules?.maxLength === "object" ? rules.maxLength.value : undefined;
  const isError = error != null;
  const currentValueLength = `${field.value || ""}`.length || 0;
  let textAreaHeight: TextAreaHeight = size;
  if (props.rows) {
    textAreaHeight = "auto";
  } else if (compactHeight) {
    textAreaHeight = "compact";
  }

  return (
    <FormErrorWrapper
      name={name}
      width={width}
      maxHeight={maxHeight}
      subtext={
        maxLengthValue != null && (
          <>
            <span className={Number(maxLengthValue) < currentValueLength ? "text-text-danger-secondary" : undefined}>
              {currentValueLength}
            </span>
            /{maxLengthValue}
          </>
        )
      }
    >
      <TextArea
        data-testid={dataTestid}
        {...props}
        appearance="field"
        height={textAreaHeight}
        hideScrollbar={scrollbar}
        invalid={isError}
        startAdornment={icon}
        {...{
          ...field,
          disabled,
          readOnly,
        }}
        ref={mergeRefs(field.ref, ref)}
        value={field.value}
        onFocus={(e) => {
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          field.onBlur();
          props.onBlur?.(e);
        }}
        onChange={(e) => {
          field.onChange(e);
          props.onChange?.(e);
        }}
      />
    </FormErrorWrapper>
  );
}
